import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    Paper,
    Radio,
    RadioGroup,
    TextField,
    Typography
} from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    Event as EventIcon,
    Lock as LockIcon,
    VerifiedUser as VerifiedUserIcon
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { createCreditEventCheckout, createMontonioEventCheckout } from "../api/eventApi";
import { getUserCredit } from "../api/planApi";
import { formatCurrency } from "../utils/currency";

const EVENT_PENDING_REF_KEY = "event_pending_order_ref";
const EVENT_LAST_REF_KEY = "event_last_order_ref";
const EVENT_CHECKOUT_CONTEXT_KEY = "event_checkout_context";
const isAbsoluteUrl = (value) => /^https?:\/\//i.test(String(value || ""));

const MONTONIO_PAYMENT_METHOD_OPTIONS = [
    { value: "paymentInitiation", label: "Bank payment" },
    { value: "cardPayments", label: "Card payment" },
    { value: "blik", label: "BLIK" },
    { value: "mobilePay", label: "MobilePay" }
];

const EventCheckoutFlowPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [placingOrder, setPlacingOrder] = useState(false);
    const [error, setError] = useState("");
    const [creditBalance, setCreditBalance] = useState(0);
    const [creditMode, setCreditMode] = useState("partial");
    const [customCredit, setCustomCredit] = useState("");
    const [selectedMontonioPaymentMethod, setSelectedMontonioPaymentMethod] = useState("paymentInitiation");

    const [event] = useState(() => {
        if (location.state?.event?.id) return location.state.event;
        const saved = localStorage.getItem(EVENT_CHECKOUT_CONTEXT_KEY);
        try {
            return saved ? JSON.parse(saved) : null;
        } catch (error) {
            return null;
        }
    });

    const affiliateId = event?.affiliateId || null;
    const eventPrice = Number(event?.price || 0);
    const paymentCountry = event?.paymentCountry || event?.affiliate?.paymentCountry || "EE";
    const paymentCurrency = event?.paymentCurrency || event?.affiliate?.paymentCurrency || event?.currency || "EUR";
    const formatMoney = (value) => formatCurrency(value, paymentCurrency);
    const isMontonioBankPaymentEnabled = event?.isMontonioBankPaymentEnabled !== false
        && event?.affiliate?.isMontonioBankPaymentEnabled !== false;
    const isMontonioCardPaymentEnabled = event?.isMontonioCardPaymentEnabled === true
        || event?.affiliate?.isMontonioCardPaymentEnabled === true;
    const isMontonioBlikPaymentEnabled = paymentCurrency === "PLN"
        && (
            event?.isMontonioBlikPaymentEnabled === true
            || event?.affiliate?.isMontonioBlikPaymentEnabled === true
        );
    const isMobilePayPaymentAvailable = paymentCountry === "FI"
        && paymentCurrency === "EUR"
        && isMontonioCardPaymentEnabled;
    const availableMontonioPaymentMethods = MONTONIO_PAYMENT_METHOD_OPTIONS.filter((method) => (
        (method.value === "paymentInitiation" && isMontonioBankPaymentEnabled)
        || (method.value === "cardPayments" && isMontonioCardPaymentEnabled)
        || (method.value === "blik" && isMontonioBlikPaymentEnabled)
        || (method.value === "mobilePay" && isMobilePayPaymentAvailable)
    ));

    useEffect(() => {
        if (!event?.id) return;
        localStorage.setItem(EVENT_CHECKOUT_CONTEXT_KEY, JSON.stringify(event));
    }, [event]);

    useEffect(() => {
        const loadCredit = async () => {
            if (!affiliateId) return;
            try {
                const response = await getUserCredit(affiliateId);
                setCreditBalance(Number(response?.credit || 0));
            } catch (loadError) {
                console.error("Failed to fetch credit:", loadError);
            }
        };
        void loadCredit();
    }, [affiliateId]);

    useEffect(() => {
        const availableValues = MONTONIO_PAYMENT_METHOD_OPTIONS
            .filter((method) => (
                (method.value === "paymentInitiation" && isMontonioBankPaymentEnabled)
                || (method.value === "cardPayments" && isMontonioCardPaymentEnabled)
                || (method.value === "blik" && isMontonioBlikPaymentEnabled)
                || (method.value === "mobilePay" && isMobilePayPaymentAvailable)
            ))
            .map((method) => method.value);

        if (!availableValues.includes(selectedMontonioPaymentMethod)) {
            setSelectedMontonioPaymentMethod(availableValues[0] || "paymentInitiation");
        }
    }, [isMontonioBankPaymentEnabled, isMontonioCardPaymentEnabled, isMontonioBlikPaymentEnabled, isMobilePayPaymentAvailable, selectedMontonioPaymentMethod]);

    const creditAmount = useMemo(() => {
        if (creditMode === "none") return 0;
        if (creditMode === "full") return Math.min(eventPrice, creditBalance);
        if (customCredit !== "") {
            const parsed = Number(customCredit);
            if (Number.isFinite(parsed) && parsed > 0) {
                return Math.min(parsed, creditBalance, eventPrice);
            }
        }
        return Math.min(creditBalance, eventPrice);
    }, [creditMode, customCredit, creditBalance, eventPrice]);

    const montonioAmount = Math.max(0, Number((eventPrice - creditAmount).toFixed(2)));

    const handleBack = () => {
        navigate("/register-training");
    };

    const redirectSameTab = (url) => {
        if (!url) return;
        if (isAbsoluteUrl(url)) {
            window.location.assign(url);
            return;
        }
        navigate(url);
    };

    const handleCheckout = async () => {
        if (!event?.id) {
            setError("Event context is missing");
            return;
        }

        setError("");
        setPlacingOrder(true);
        try {
            if (creditMode === "full" && montonioAmount === 0) {
                const response = await createCreditEventCheckout(event.id);
                localStorage.setItem(EVENT_LAST_REF_KEY, response.merchantReference || "");
                localStorage.setItem(EVENT_PENDING_REF_KEY, response.merchantReference || "");
                const returnUrl = response?.redirectUrl
                    || (response?.merchantReference
                        ? `/after-checkout?event=1&ref=${encodeURIComponent(response.merchantReference)}`
                        : "/after-checkout?event=1");
                redirectSameTab(returnUrl);
                return;
            }

            const response = await createMontonioEventCheckout(event.id, {
                creditMode,
                creditAmount,
                paymentMethod: selectedMontonioPaymentMethod
            });
            localStorage.setItem(EVENT_LAST_REF_KEY, response.merchantReference || "");
            localStorage.setItem(EVENT_PENDING_REF_KEY, response.merchantReference || "");

            if (response?.paymentUrl) {
                window.location.assign(response.paymentUrl);
                return;
            }

            const returnUrl = response?.redirectUrl
                || (response?.merchantReference
                    ? `/after-checkout?event=1&ref=${encodeURIComponent(response.merchantReference)}`
                    : "/after-checkout?event=1");
            redirectSameTab(returnUrl);
        } catch (checkoutError) {
            console.error("Event checkout error:", checkoutError);
            setError(checkoutError?.response?.data?.message || "Could not start event checkout");
        } finally {
            setPlacingOrder(false);
        }
    };

    if (!event?.id) {
        return (
            <Container maxWidth="md" sx={{ py: { xs: 1.5, md: 3 } }}>
                <Alert severity="warning">Event checkout context is missing. Open checkout from the Events view.</Alert>
                <Button variant="outlined" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }} onClick={handleBack}>
                    Back to events
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 1.5, md: 3 } }}>
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, md: 3 },
                    mb: 2,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    background: "linear-gradient(180deg, rgba(25,118,210,0.08) 0%, rgba(255,255,255,1) 45%)"
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <EventIcon color="primary" />
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                Checkout
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Review your event ticket and complete payment securely.
                            </Typography>
                        </Box>
                    </Box>
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
                        Back to events
                    </Button>
                </Box>
            </Paper>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            <Grid container spacing={2.5}>
                <Grid item xs={12} md={8}>
                    <Card sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                                Order items
                            </Typography>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "2fr auto auto" },
                                    gap: 1.5,
                                    alignItems: "center",
                                    py: 1.5,
                                    borderBottom: "1px solid #eee"
                                }}
                            >
                                <Box>
                                    <Typography sx={{ fontWeight: 600 }}>{event.title}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Event ticket
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {new Date(event.eventStartAt).toLocaleString("et-EE")} - {new Date(event.eventEndAt).toLocaleString("et-EE")}
                                    </Typography>
                                </Box>

                                <Box sx={{ justifySelf: { xs: "start", sm: "center" } }}>
                                    <Chip size="small" label="Qty 1" />
                                </Box>

                                <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                                    <Typography sx={{ fontWeight: 700 }}>
                                        {formatMoney(eventPrice)}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card
                        sx={{
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            position: { md: "sticky" },
                            top: { md: 84 }
                        }}
                    >
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                                Order summary
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Available credit: {formatMoney(creditBalance)}
                            </Typography>

                            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                                <Chip size="small" label="1 item" />
                                <Chip size="small" color="primary" label={`Subtotal ${formatMoney(eventPrice)}`} />
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Payment method
                            </Typography>
                            <FormControl>
                                <RadioGroup
                                    sx={{ gap: 0.5 }}
                                    value={creditMode}
                                    onChange={(e) => setCreditMode(e.target.value)}
                                >
                                    <FormControlLabel value="partial" control={<Radio />} label="Use credit first" />
                                    <FormControlLabel value="none" control={<Radio />} label="Do not use credit" />
                                    <FormControlLabel value="full" control={<Radio />} label="Full credit only" />
                                </RadioGroup>
                            </FormControl>

                            {creditMode === "partial" ? (
                                <TextField
                                    sx={{ mt: 1.5 }}
                                    label="Custom credit amount (optional)"
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={customCredit}
                                    onChange={(e) => setCustomCredit(e.target.value)}
                                />
                            ) : null}

                            {montonioAmount > 0 ? (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Montonio payment method
                                    </Typography>
                                    {availableMontonioPaymentMethods.length > 1 ? (
                                        <FormControl>
                                            <RadioGroup
                                                sx={{ gap: 0.5 }}
                                                value={selectedMontonioPaymentMethod}
                                                onChange={(e) => setSelectedMontonioPaymentMethod(e.target.value)}
                                            >
                                                {availableMontonioPaymentMethods.map((method) => (
                                                    <FormControlLabel
                                                        key={method.value}
                                                        value={method.value}
                                                        control={<Radio />}
                                                        label={method.label}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        </FormControl>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            {availableMontonioPaymentMethods[0]?.label || "Bank payment"}
                                        </Typography>
                                    )}
                                </Box>
                            ) : null}

                            <Box sx={{ mt: 2.5, mb: 2 }}>
                                <Typography variant="body2" sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                    <span>Subtotal</span>
                                    <strong>{formatMoney(eventPrice)}</strong>
                                </Typography>
                                <Typography variant="body2" sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                    <span>Credit</span>
                                    <strong>- {formatMoney(creditAmount)}</strong>
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="subtitle1" sx={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
                                    <span>To pay online</span>
                                    <span>{formatMoney(montonioAmount)}</span>
                                </Typography>
                            </Box>

                            <Alert severity="info" icon={<VerifiedUserIcon />} sx={{ mb: 2 }}>
                                Ticket purchase automatically registers you to the event.
                            </Alert>

                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                startIcon={<LockIcon />}
                                onClick={handleCheckout}
                                disabled={placingOrder}
                            >
                                {placingOrder ? "Processing..." : "Continue"}
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
};

export default EventCheckoutFlowPage;
